// Κινήσεις ταμείου — backend routes/cashMovements.js (migration 277). Η καταχώριση είναι ιδεμποτική στο
// clientKey: ό,τι δεν φέρνει σαφή απάντηση (δίκτυο, 5xx) ξαναστέλνεται με το ΙΔΙΟ κλειδί και ο server
// επιστρέφει την ίδια κίνηση — ποτέ δεύτερη.
//
// Διαχειριστικό (Φάση 2, μόνο διαχειριστής / χρήστης οργανισμού): λίστα με φίλτρα και σύνολα, CSV λογιστή,
// κατηγορίες εξόδων (προσθήκη, μετονομασία, σειρά, απενεργοποίηση — ποτέ διαγραφή).

import { API_BASE_URL } from '../../config/api';
import type { CashMovement, CashMovementKind, CashMovementsTotals } from '../../../types/registerReport';

export interface ExpenseCategory {
  id: number;
  name: string;
  active: boolean;
}

export interface CashMovementsConfig {
  enabled: boolean;
  canCreate: boolean;
  canVoid: boolean;
  categories: ExpenseCategory[];
  /** Ανώτατο ποσό ανά κίνηση (€). */
  maxAmount: number;
  /** Έξοδο σε μετρητά από αυτό το ποσό και πάνω: προειδοποίηση (€). */
  cashWarnAmount: number;
}

export interface CreateCashMovementBody {
  kind: CashMovementKind;
  /** Σε ευρώ με τελεία, π.χ. «12.50». */
  amount: string;
  categoryId?: number | null;
  note?: string;
  supplierName?: string;
  supplierVat?: string;
  documentNumber?: string;
  clientKey: string;
}

export class CashMovementApiError extends Error {
  status: number;
  code: string | null;
  /** Όλο το σώμα της απάντησης (π.χ. categoryId / active στο 409 category_exists). */
  body: Record<string, unknown>;
  constructor(status: number, body: Record<string, unknown>) {
    super(String(body.error || body.message || `HTTP ${status}`));
    this.status = status;
    this.code = typeof body.code === 'string' ? body.code : null;
    this.body = body;
  }
}

const authHeaders = (json: boolean) => ({
  Authorization: `Bearer ${localStorage.getItem('xentrix_token')}`,
  ...(json ? { 'Content-Type': 'application/json' } : {}),
});

async function call<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(`${API_BASE_URL}/api${path}`, {
    method: init.method || 'GET',
    headers: authHeaders(init.body !== undefined),
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  const body = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
  if (!res.ok) throw new CashMovementApiError(res.status, body);
  return body as unknown as T;
}

/* ───────────── διαχειριστικό ───────────── */

export type CashMovementStatus = '' | 'active' | 'voided';

/** Φίλτρα της λίστας/του CSV· '' = χωρίς φίλτρο. from/to: μέρες Ελλάδας «ΕΕΕΕ-ΜΜ-ΗΗ», και οι δύο μέσα. */
export interface CashMovementsFilters {
  from: string;
  to: string;
  kind: '' | CashMovementKind;
  categoryId: string;
  status: CashMovementStatus;
}

export interface CashMovementsListTotals extends CashMovementsTotals {
  /** Καταθέσεις − έξοδα − αναλήψεις: η επίδραση στο συρτάρι. */
  net: number;
}

export interface CategoryTotal {
  categoryId: number | null;
  name: string | null;
  active: boolean;
  count: number;
  amount: number;
}

export interface CashMovementsPage {
  items: CashMovement[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
  /** Όλου του φίλτρου (όχι της σελίδας)· οι ακυρωμένες μόνο στο voided. */
  totals: CashMovementsListTotals;
  /** Ενεργά έξοδα ανά κατηγορία, μεγαλύτερα πρώτα. */
  byCategory: CategoryTotal[];
}

export interface AdminCategory {
  id: number;
  name: string;
  active: boolean;
  sortOrder: number;
}

export interface AdminCategories {
  /** organization: κοινές σε όλα τα καταστήματα του οργανισμού· store: ανεξάρτητο κατάστημα. */
  scope: 'organization' | 'store';
  categories: AdminCategory[];
  /** Η κατηγορία που μόλις προστέθηκε/άλλαξε. */
  categoryId?: number;
}

/** Το query της λίστας / του CSV (χωρίς τα κενά φίλτρα). */
export function movementsQuery(f: CashMovementsFilters, page?: { page: number; limit: number }): string {
  const params = new URLSearchParams();
  if (f.from) params.set('from', f.from);
  if (f.to) params.set('to', f.to);
  if (f.kind) params.set('kind', f.kind);
  if (f.categoryId) params.set('categoryId', f.categoryId);
  if (f.status) params.set('status', f.status);
  if (page) {
    params.set('page', String(page.page));
    params.set('limit', String(page.limit));
  }
  return params.toString();
}

/** Το όνομα του αρχείου από το Content-Disposition (αλλιώς το δοσμένο). */
export function fileNameOf(disposition: string | null, fallback: string): string {
  const m = /filename="?([^";]+)"?/i.exec(disposition || '');
  return m ? m[1] : fallback;
}

async function download(path: string, fallbackName: string): Promise<{ blob: Blob; fileName: string }> {
  const res = await fetch(`${API_BASE_URL}/api${path}`, { headers: authHeaders(false) });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new CashMovementApiError(res.status, (data && typeof data === 'object' ? data : {}) as Record<string, unknown>);
  }
  return { blob: await res.blob(), fileName: fileNameOf(res.headers.get('Content-Disposition'), fallbackName) };
}

export const cashMovementsApi = {
  config: (storeId: number) => call<CashMovementsConfig>(`/stores/${storeId}/cash-movements/config`),
  create: (storeId: number, body: CreateCashMovementBody) =>
    call<{ movement: CashMovement; replay: boolean }>(`/stores/${storeId}/cash-movements`, { method: 'POST', body }),
  void: (storeId: number, id: number, reason: string) =>
    call<{ movement: CashMovement; replay: boolean }>(`/stores/${storeId}/cash-movements/${id}/void`, { method: 'POST', body: { reason } }),

  list: (storeId: number, f: CashMovementsFilters, page: { page: number; limit: number }) =>
    call<CashMovementsPage>(`/stores/${storeId}/cash-movements?${movementsQuery(f, page)}`),
  exportCsv: (storeId: number, f: CashMovementsFilters) => {
    const q = movementsQuery(f);
    return download(`/stores/${storeId}/cash-movements/export.csv${q ? `?${q}` : ''}`, 'kiniseis-tameiou.csv');
  },
  categories: (storeId: number) => call<AdminCategories>(`/stores/${storeId}/cash-movements/categories`),
  addCategory: (storeId: number, name: string) =>
    call<AdminCategories>(`/stores/${storeId}/cash-movements/categories`, { method: 'POST', body: { name } }),
  updateCategory: (storeId: number, id: number, patch: { name?: string; active?: boolean }) =>
    call<AdminCategories>(`/stores/${storeId}/cash-movements/categories/${id}`, { method: 'PATCH', body: patch }),
  reorderCategories: (storeId: number, ids: number[]) =>
    call<AdminCategories>(`/stores/${storeId}/cash-movements/categories/order`, { method: 'PUT', body: { ids } }),
};

/** Σαφής απάντηση του server (4xx): νέα προσπάθεια = νέο κλειδί. Δίκτυο / 5xx: ίδιο κλειδί. */
export const isDefiniteRejection = (e: unknown) =>
  e instanceof CashMovementApiError && e.status >= 400 && e.status < 500;
